import React, { useState, useEffect } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Chip, IconButton, TablePagination, Button, Typography, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { HousekeepingService } from '../../api/services/housekeepingService';
import { StaffService } from '../../api/services/staffService';
import TaskDetailsModal from '../../components/TaskDetailsModal';
import { toast } from '../../utils/toast';
import ExportFormatModal from '../../components/ExportFormatModal';
import { exportClientSide } from '../../utils/exportUtils';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { extractTotalCount, standardFilterContainerSx, standardFilterSelectSx, standardFilterInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../components/common/tableStyles';

export default function TaskHistoryTab({ tenantId, refreshKey }) {
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [detailsModal, setDetailsModal] = useState({ open: false, taskId: null });
    const [taskFilters, setTaskFilters] = useState({ status: '', priority: '', staffMember: '', date: '' });
    const [staffList, setStaffList] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const res = await StaffService.getStaff({ noPagination: true });
                const list = Array.isArray(res)
                    ? res
                    : (Array.isArray(res?.data)
                        ? res.data
                        : (Array.isArray(res?.staff)
                            ? res.staff
                            : (Array.isArray(res?.data?.staff)
                                ? res.data.staff
                                : (Array.isArray(res?.staffMembers)
                                    ? res.staffMembers
                                    : (Array.isArray(res?.data?.staffMembers)
                                        ? res.data.staffMembers
                                        : [])))));
                setStaffList(list.filter(s => !s.status || s.status.toLowerCase() === 'active'));
            } catch (error) {
                console.error("Failed to fetch staff list:", error);
            }
        };
        fetchStaff();
    }, [tenantId]);

    // Build complete list of staff including any already assigned to tasks
    const displayedStaff = [...staffList];
    tasks.forEach(t => {
        const staffObj = t.assignedStaff;
        const staffName = staffObj?.fullName || staffObj?.name || (typeof t.assignedTo === 'string' ? t.assignedTo : '') || t.assignedStaffName;
        const staffId = staffObj?.id || staffObj?._id || t.assignedStaffId || staffName;
        if (staffName && !displayedStaff.some(s => (s.id && s.id === staffId) || (s.fullName || s.name) === staffName)) {
            displayedStaff.push({
                id: staffId,
                fullName: staffName,
                name: staffName,
            });
        }
    });

    const fetchTasks = async () => {
        setIsLoading(true);
        try {
            const selectedStaff = displayedStaff.find(s => (s.id || s._id || s.name || s.fullName) === taskFilters.staffMember);
            const staffName = selectedStaff?.fullName || selectedStaff?.name || taskFilters.staffMember;
            const staffId = selectedStaff?.id || selectedStaff?._id || taskFilters.staffMember;

            const params = {
                page,
                limit: rowsPerPage,
                status: taskFilters.status || undefined,
                priority: taskFilters.priority || undefined,
                staffMember: staffName || undefined,
                assignedStaffId: staffId || undefined,
                date: taskFilters.date || undefined,
            };
            const res = await HousekeepingService.getAllTasks(tenantId, params);
            const apiTasks = res?.data?.tasks || res?.tasks || res?.data || res || [];
            setTasks(Array.isArray(apiTasks) ? apiTasks : []);
            setTotal(extractTotalCount(res, apiTasks));
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [tenantId, page, rowsPerPage, refreshKey, taskFilters]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleTaskFilterChange = (e) => {
        setTaskFilters({ ...taskFilters, [e.target.name]: e.target.value });
        setPage(0);
    };

    const handleExport = (format) => {
        setExportModalOpen(false);
        try {
            exportClientSide(format, tasks, 'task_history');
            toast.success('Export successful');
        } catch (error) {
            toast.error(error.message || 'Export failed');
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'Urgent':
                return { bgcolor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' };
            case 'High':
                return { bgcolor: '#ffedd5', color: '#ea580c', border: '1px solid #fed7aa' };
            case 'Medium':
                return { bgcolor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' };
            case 'Low':
                return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
            default:
                return { bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Completed':
                return { bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' };
            case 'In Progress':
                return { bgcolor: '#e3f2fd', color: '#1976d2', border: '1px solid #bbdefb' };
            case 'Inspection':
                return { bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2' };
            case 'Cancelled':
                return { bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2' };
            case 'Ready':
            default:
                return { bgcolor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' };
        }
    };

    const paginatedTasks = tasks;

    return (
        <Box>
            <Box sx={standardFilterContainerSx}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Task History</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select name="status" value={taskFilters.status} displayEmpty onChange={handleTaskFilterChange} sx={standardFilterSelectSx}>
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="Completed">Completed</MenuItem>
                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select name="priority" value={taskFilters.priority} displayEmpty onChange={handleTaskFilterChange} sx={standardFilterSelectSx}>
                            <MenuItem value="">All Priorities</MenuItem>
                            <MenuItem value="Urgent">Urgent</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                            name="staffMember"
                            value={taskFilters.staffMember}
                            displayEmpty
                            onChange={handleTaskFilterChange}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="">All Staff</MenuItem>
                            {displayedStaff.map((s) => {
                                const name = s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email || s.employeeId || 'Staff';
                                const val = s.id || s._id || s.name || s.fullName;
                                return (
                                    <MenuItem key={s.id || s._id || name} value={val}>
                                        {name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <TextField size="small" type="date" name="date" value={taskFilters.date} onChange={handleTaskFilterChange} sx={standardFilterInputSx} />
                    
                    {(taskFilters.status || taskFilters.priority || taskFilters.staffMember || taskFilters.date) && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => setTaskFilters({ status: '', priority: '', staffMember: '', date: '' })}>Reset</Button>
                    )}
                    
                    <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} sx={standardSecondaryButtonSx} onClick={() => setExportModalOpen(true)}>
                        Export Options
                    </Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleExport}
                        isExporting={false}
                        title="Export Task History"
                    />
                </Box>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Room</TableCell>
                            <TableCell>Task Type</TableCell>
                            <TableCell>Priority</TableCell>
                            <TableCell>Assigned To</TableCell>
                            <TableCell>Completed/Due</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                        ) : paginatedTasks.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No historical tasks available.</TableCell></TableRow>
                        ) : paginatedTasks.map((task) => (
                            <TableRow key={task.id} hover>
                                <TableCell><b>{task.roomNumber || task.room || task.roomId || '-'}</b></TableCell>
                                <TableCell>{task.taskType || task.type || task.taskTypeId || '-'}</TableCell>
                                <TableCell>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        ...getPriorityStyle(task.priority),
                                        px: 1.25,
                                        py: 0.35,
                                        borderRadius: '16px',
                                        fontWeight: '700',
                                        fontSize: '0.75rem',
                                    }}>
                                        {task.priority || 'Medium'}
                                    </Box>
                                </TableCell>
                                <TableCell>{task.assignedStaff?.fullName || task.assignedStaff?.name || task.assignedTo || task.assignedStaffId || 'Unassigned'}</TableCell>
                                <TableCell>{task.dueBy ? new Date(task.dueBy).toLocaleString('en-US') : '-'}</TableCell>
                                <TableCell>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        ...getStatusStyle(task.status),
                                        px: 1.25,
                                        py: 0.35,
                                        borderRadius: '16px',
                                        fontWeight: '700',
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                    }}>
                                        {task.status || 'Completed'}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="info" onClick={() => setDetailsModal({ open: true, taskId: task.id })}><VisibilityIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={tasks.length === 0 ? 0 : total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
            <TaskDetailsModal open={detailsModal.open} onClose={() => setDetailsModal({ open: false, taskId: null })} taskId={detailsModal.taskId} />
        </Box>
    );
}
